// @wavvest/shared-services — API client factory with DI for auth
// Extracted from: apps/*/src/services/service.ts
// Both portals share identical response interceptor logic and error handling.
// Auth differs (Amplify vs localStorage token) so it's injected via AuthInterceptor.

import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

// --- Types ---

export interface AuthInterceptor {
  /** Attach auth token to outgoing requests */
  onRequest: (
    config: InternalAxiosRequestConfig,
  ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;
  /** Called on 401 with AUTH_TOKEN_INVALID_OR_EXPIRED or NO_AUTH_TOKEN */
  onUnauthorized?: () => void;
}

export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
  authInterceptor: AuthInterceptor;
  /**
   * Predicate to decide whether an envelope-style response (object containing
   * a `status` or `kind` field) is treated as success. Defaults to the legacy
   * portal behavior of accepting only 200 and 204. Callers can pass
   * `(s) => s >= 200 && s < 300` for full 2xx success.
   */
  successStatusPredicate?: (status: number) => boolean;
  /**
   * Behavior on adapter timeout errors:
   *   - false (default): reject immediately (matches client portal).
   *   - true:           retry the original request on this same instance.
   *   - AxiosInstance:  retry the original request on the provided instance
   *                     (matches advisor portal's `RiaAdminInstance`, which
   *                     historically retried via `ServiceInstance`).
   */
  retryOnTimeout?: boolean | AxiosInstance;
  /**
   * 401 body parsing strategy. Defaults to true: legacy behavior assumes the
   * server returns a well-formed `{ detail: { kind, message } }` body. Set
   * false to wrap parsing in try/catch and fall back to a generic
   * "Unauthorized" message.
   */
  strictUnauthorized?: boolean;
  /**
   * Console method used to surface mapped error messages.
   * Defaults to `console.log` to match legacy portal behavior.
   */
  logLevel?: 'log' | 'error';
  /**
   * When true, overwrite `error.message` with the mapped message before
   * rejecting. Defaults to false to match legacy portal behavior, which only
   * logged the mapped message and rejected the original error.
   */
  mutateErrorMessage?: boolean;
}

type TimeoutRetriableRequestConfig = InternalAxiosRequestConfig & {
  _retryOnTimeoutCount?: number;
};

const timeoutErrorCodes = new Set(['ECONNABORTED', 'ETIMEDOUT']);
const timeoutRetryIdempotentMethods = new Set(['get', 'head', 'options', 'put', 'delete']);
const idempotencyHeaderNames = new Set(['idempotency-key', 'x-idempotency-key']);
const maxTimeoutRetries = 1;

function isTimeoutError(error: AxiosError): boolean {
  return timeoutErrorCodes.has(error.code ?? '') && error.message.toLowerCase().includes('timeout');
}

function hasStableIdempotencyKey(headers: unknown): boolean {
  const maybeHeadersWithGetter = headers as { get?: (name: string) => unknown } | null | undefined;
  if (typeof maybeHeadersWithGetter?.get === 'function') {
    return Array.from(idempotencyHeaderNames).some((headerName) => {
      const value = maybeHeadersWithGetter.get?.(headerName);
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  }

  if (!headers || typeof headers !== 'object') {
    return false;
  }

  return Object.entries(headers as Record<string, unknown>).some(
    ([headerName, value]) =>
      idempotencyHeaderNames.has(headerName.toLowerCase()) &&
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '',
  );
}

function canRetryTimedOutRequest(reqConfig: InternalAxiosRequestConfig): boolean {
  const method = reqConfig.method?.toLowerCase() ?? 'get';
  return timeoutRetryIdempotentMethods.has(method) || hasStableIdempotencyKey(reqConfig.headers);
}

// --- Abort controller registry ---

const controllerMap = new Map<string, AbortController>();

export function cancelRequest(key: string): void {
  const controller = controllerMap.get(key);
  if (controller) {
    controller.abort();
    controllerMap.delete(key);
  }
}

export function cancelAllRequests(): void {
  controllerMap.forEach((controller) => controller.abort());
  controllerMap.clear();
}

// --- Shared error status messages ---

const errorStatusMessages: Record<number, string> = {
  400: 'Bad Request',
  403: 'You are not authorized to access this page. Please log in again.',
  408: 'Request Timeout. Please try logging in again.',
  500: 'Internal Server Error. Please try logging in again.',
  501: 'Not Implemented. Please try logging in again.',
  502: 'Bad Gateway. Please try logging in again.',
  503: 'Service Unavailable. Please try logging in again.',
  504: 'Gateway Timeout. Please try logging in again.',
  505: 'HTTP Version Not Supported',
};

// --- Factory ---

export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const instance = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout ?? 30_000,
    headers: config.headers ?? {
      'Content-Type': 'application/json',
    },
  });

  const successStatusPredicate =
    config.successStatusPredicate ?? ((status: number) => status === 200 || status === 204);
  const strictUnauthorized = config.strictUnauthorized ?? true;
  const logFn = (config.logLevel ?? 'log') === 'error' ? console.error : console.log;
  const mutateErrorMessage = config.mutateErrorMessage ?? false;

  const retryTimedOutRequest = (error: AxiosError) => {
    if (
      !config.retryOnTimeout ||
      !isTimeoutError(error) ||
      !error.config ||
      !canRetryTimedOutRequest(error.config)
    ) {
      return null;
    }

    const originalRequest = error.config as TimeoutRetriableRequestConfig;
    const retryCount = originalRequest._retryOnTimeoutCount ?? 0;
    if (retryCount >= maxTimeoutRetries) {
      return null;
    }

    originalRequest._retryOnTimeoutCount = retryCount + 1;
    const retryInstance = config.retryOnTimeout === true ? instance : config.retryOnTimeout;
    return retryInstance.request(originalRequest);
  };

  // Request interceptor — delegate auth to consumer, wire abort controllers
  instance.interceptors.request.use(
    (reqConfig) => {
      // Wire abort controller if a requestKey is provided via metadata
      const requestKey = (reqConfig as { requestKey?: string }).requestKey;
      if (requestKey) {
        cancelRequest(requestKey); // Cancel any existing request with this key
        const controller = new AbortController();
        reqConfig.signal = controller.signal;
        controllerMap.set(requestKey, controller);
      }
      return config.authInterceptor.onRequest(reqConfig);
    },
    (error: AxiosError) => Promise.reject(error),
  );

  // Response interceptor — shared across both portals
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const { status, data } = response;
      if (typeof data === 'object' && data !== null && ('status' in data || 'kind' in data)) {
        if (successStatusPredicate(status)) {
          return data;
        } else {
          return Promise.reject(data);
        }
      }
      return data;
    },
    (error: AxiosError) => {
      const timeoutRetry = retryTimedOutRequest(error);
      if (timeoutRetry) {
        return timeoutRetry;
      }

      const { response, message: errorMsg } = error;
      const status = response?.status;
      const { data } = response || {};
      let errorMessage: string = errorMsg || 'Request failed';

      if (status) {
        if (status === 401) {
          if (strictUnauthorized) {
            const { kind, message: detailMessage } = (
              data as { detail: { kind: string; message: string } }
            ).detail;
            errorMessage = detailMessage;
            if (kind === 'AUTH_TOKEN_INVALID_OR_EXPIRED' || kind === 'NO_AUTH_TOKEN') {
              config.authInterceptor.onUnauthorized?.();
            }
          } else {
            try {
              const { kind, message: detailMessage } = (
                data as { detail: { kind: string; message: string } }
              ).detail;
              errorMessage = detailMessage;
              if (kind === 'AUTH_TOKEN_INVALID_OR_EXPIRED' || kind === 'NO_AUTH_TOKEN') {
                config.authInterceptor.onUnauthorized?.();
              }
            } catch {
              errorMessage = 'Unauthorized';
            }
          }
        } else if (status === 404) {
          errorMessage = `Request URL error: ${response?.config.url}`;
        } else if (errorStatusMessages[status]) {
          errorMessage = errorStatusMessages[status];
        }
      }

      if (error.message.includes('Network Error')) {
        errorMessage = 'Network Connection Error';
      } else if (isTimeoutError(error)) {
        errorMessage = 'Request Timeout';
      }

      logFn(errorMessage);
      if (mutateErrorMessage) {
        error.message = errorMessage;
      }
      return Promise.reject(error);
    },
  );

  return instance;
}
